/**
 * Dateianhänge an einem Beleg.
 *
 * Gedacht für Schriftstücke, die zum Vorgang gehören, aber nicht von uns
 * stammen: die Bestellung des Kunden als PDF, ein unterschriebener
 * Lieferschein, Schriftverkehr. Sie hängen am Beleg, sind aber nicht Teil
 * von ihm — deshalb dürfen sie auch nach dem Festschreiben noch ergänzt
 * werden, ohne dessen Prüfsumme zu berühren.
 */

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { formatTimestamp } from '@erp/shared';

import { api, ApiError, apiUrl } from '../api/client';
import { useAuth } from '../auth';
import { useToast } from './toast';
import { Alert, Badge, EmptyState } from './ui';

type Attachment = {
  id: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  checksum: string | null;
  attachmentType: string;
  isOriginal: boolean;
  createdAt: string;
};

const TYPE_LABELS: Record<string, string> = {
  original: 'Original',
  generated_pdf: 'Beleg-PDF',
  generated_xml: 'E-Rechnung',
  scan: 'Scan',
  other: 'Sonstiges',
};

/** Vom System erzeugte Dateien lassen sich nicht entfernen. */
const GENERATED = new Set(['generated_pdf', 'generated_xml']);

function formatSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

function fileIcon(mimeType: string): string {
  if (mimeType === 'application/pdf') return '▤';
  if (mimeType.startsWith('image/')) return '▣';
  if (mimeType.includes('xml')) return '⌗';
  if (mimeType === 'message/rfc822') return '✉';
  return '▢';
}

export function DocumentAttachments({
  documentId,
  documentPosted,
}: {
  documentId: string;
  documentPosted: boolean;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { can } = useAuth();
  const mayWrite = can('sales:write') || can('purchase:write');

  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [uploadType, setUploadType] = useState<'original' | 'other'>('original');

  /**
   * Ablegen auf der gesamten Seite.
   *
   * Ein kleines Feld am Seitenende zu treffen ist mühsam, und wer
   * danebentrifft, öffnet die Datei im Browser statt sie anzuhängen.
   * Deshalb nimmt die ganze Belegseite die Datei entgegen und blendet
   * beim Ziehen eine Fläche über den Inhalt.
   */
  const dragDepth = useRef(0);

  const { data, isLoading } = useQuery({
    queryKey: ['attachments', documentId],
    queryFn: () =>
      api.get<{ attachments: Attachment[] }>(`/api/documents/${documentId}/attachments`),
  });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append('attachmentType', uploadType);
      form.append('file', file, file.name);

      // FormData darf nicht durch den JSON-Client laufen - der Browser muss
      // die Multipart-Grenze selbst setzen.
      const response = await fetch(apiUrl(`/api/documents/${documentId}/attachments`), {
        method: 'POST',
        body: form,
        credentials: 'same-origin',
      });

      if (!response.ok) {
        const payload = (await response.json().catch(() => ({}))) as { message?: string };
        throw new ApiError(
          response.status,
          'UPLOAD',
          payload.message ?? 'Die Datei konnte nicht angehängt werden.',
        );
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attachments', documentId] });
      toast.success('Die Datei wurde angehängt.');
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Der Upload ist fehlgeschlagen.'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api/attachments/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attachments', documentId] });
      toast.success('Der Anhang wurde entfernt.');
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Entfernen fehlgeschlagen.'),
  });

  function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    // Einzeln hochladen, damit ein fehlerhafter Anhang die übrigen nicht mitreißt.
    for (const file of Array.from(files)) upload.mutate(file);
  }

  useEffect(() => {
    if (!mayWrite) return;

    /** Enthält der Zug überhaupt Dateien? Text-Markierungen ignorieren. */
    function hasFiles(event: DragEvent): boolean {
      return Array.from(event.dataTransfer?.types ?? []).includes('Files');
    }

    /**
     * Ist ein Dialog offen (z. B. der Versanddialog), gehört der Zug dem
     * Dialog. Die seitenweite Ablage hält sich dann zurück, verhindert aber
     * weiterhin, dass der Browser die Datei selbst öffnet.
     */
    function dialogOpen(): boolean {
      return Boolean(window.document.querySelector('.dialog-backdrop'));
    }

    function onDragEnter(event: DragEvent) {
      if (!hasFiles(event) || dialogOpen()) return;
      // dragenter/dragleave feuern auch beim Wechsel zwischen
      // Kindelementen. Ohne Zähler flackert die Anzeige.
      dragDepth.current += 1;
      setDragging(true);
    }

    function onDragOver(event: DragEvent) {
      if (!hasFiles(event)) return;
      // Ohne preventDefault öffnet der Browser die Datei im Tab.
      event.preventDefault();
      if (dialogOpen()) return;
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
    }

    function onDragLeave(event: DragEvent) {
      if (!hasFiles(event) || dialogOpen()) return;
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (dragDepth.current === 0) setDragging(false);
    }

    function onDrop(event: DragEvent) {
      if (!hasFiles(event)) return;
      event.preventDefault();
      dragDepth.current = 0;
      setDragging(false);
      if (dialogOpen()) return;
      handleFiles(event.dataTransfer?.files ?? null);
    }

    window.addEventListener('dragenter', onDragEnter);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('drop', onDrop);

    return () => {
      window.removeEventListener('dragenter', onDragEnter);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('drop', onDrop);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mayWrite, uploadType, documentId]);

  const attachments = data?.attachments ?? [];

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <h2>Dokumente</h2>
          <div className="faint" style={{ fontSize: 12.5 }}>
            Bestellungen, Schriftverkehr und Scans zu diesem Vorgang
          </div>
        </div>
        {mayWrite ? (
          <div className="row" style={{ gap: 8 }}>
            <select
              className="select input"
              style={{ width: 190 }}
              value={uploadType}
              onChange={(event) => setUploadType(event.target.value as 'original' | 'other')}
            >
              <option value="original">Original (aufbewahrungspflichtig)</option>
              <option value="other">Nebendokument</option>
            </select>
            <button type="button" className="btn btn-sm" onClick={() => inputRef.current?.click()}>
              Datei auswählen
            </button>
          </div>
        ) : null}
      </div>

      {mayWrite ? (
        <div className="card-body" style={{ paddingTop: 12, paddingBottom: 12 }}>
          <input
            ref={inputRef}
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={(event) => {
              handleFiles(event.target.files);
              event.target.value = '';
            }}
          />

          <button
            type="button"
            className="drop-zone"
            onClick={() => inputRef.current?.click()}
          >
            {upload.isPending
              ? 'Datei wird übertragen …'
              : 'Datei hier ablegen oder klicken zum Auswählen'}
            <span className="drop-zone-hint">
              PDF, Bilder, XML, Office-Dokumente, E-Mails · bis 20 MB · irgendwo auf der Seite
              ablegen genügt
            </span>
          </button>
        </div>
      ) : null}

      {/* Ablagefläche über der gesamten Seite, solange eine Datei gezogen wird. */}
      {dragging ? (
        <div className="drop-overlay">
          <div className="drop-overlay-box">
            <div style={{ fontSize: 28, marginBottom: 8 }} aria-hidden="true">
              ⤓
            </div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>Datei loslassen</div>
            <div style={{ color: 'var(--text-muted)', marginTop: 4 }}>
              wird an diesen Beleg gehängt als{' '}
              {uploadType === 'original' ? 'Original' : 'Nebendokument'}
            </div>
          </div>
        </div>
      ) : null}

      {isLoading ? null : attachments.length === 0 ? (
        <EmptyState title="Noch keine Dokumente" />
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th className="shrink"></th>
                <th>Datei</th>
                <th className="shrink">Art</th>
                <th className="numeric">Größe</th>
                <th className="shrink">Abgelegt</th>
                <th className="shrink"></th>
              </tr>
            </thead>
            <tbody>
              {attachments.map((attachment) => {
                const generated = GENERATED.has(attachment.attachmentType);
                const locked = generated || (attachment.isOriginal && documentPosted);

                return (
                  <tr key={attachment.id}>
                    <td className="faint" style={{ fontSize: 15 }}>
                      {fileIcon(attachment.mimeType)}
                    </td>
                    <td>
                      <a href={apiUrl(`/api/attachments/${attachment.id}/download`)} target="_blank" rel="noreferrer">
                        {attachment.fileName}
                      </a>
                      {attachment.checksum ? (
                        <div className="faint mono" style={{ fontSize: 11 }}>
                          SHA-256 {attachment.checksum.slice(0, 16)}…
                        </div>
                      ) : null}
                    </td>
                    <td>
                      <Badge kind={generated ? 'accent' : attachment.isOriginal ? 'success' : 'neutral'}>
                        {TYPE_LABELS[attachment.attachmentType] ?? attachment.attachmentType}
                      </Badge>
                    </td>
                    <td className="numeric muted">{formatSize(attachment.sizeBytes)}</td>
                    <td className="muted" style={{ whiteSpace: 'nowrap' }}>
                      {formatTimestamp(attachment.createdAt)}
                    </td>
                    <td>
                      {mayWrite && !locked ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() => remove.mutate(attachment.id)}
                        >
                          Entfernen
                        </button>
                      ) : (
                        <span
                          className="faint"
                          title={
                            generated
                              ? 'Vom System erzeugte Belegausgabe'
                              : 'Aufbewahrungspflichtiges Original an einem festgeschriebenen Beleg'
                          }
                        >
                          gesperrt
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {attachments.some((attachment) => attachment.isOriginal) && documentPosted ? (
        <div className="card-body" style={{ paddingTop: 0 }}>
          <Alert kind="info">
            Als Original hinterlegte Dokumente sind Handelsbriefe und sechs bis zehn Jahre
            aufzubewahren. Sie lassen sich an einem festgeschriebenen Beleg nicht mehr entfernen.
          </Alert>
        </div>
      ) : null}
    </div>
  );
}
